import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllCustomers, getAllProperties, createCustomLoan, getPropertyEscrowPreview } from '../api';
import { useConfig } from '../context/ConfigContext';
import { formatOrdinalDay } from '../utils/formatters';
// The Monthly Payment field on this form is PRINCIPAL AND INTEREST -- the term
// is back-solved from it. What the buyer will owe is that plus escrow.
import { totalMonthlyPayment } from '../utils/monthlyPayment';
import { feeBreakdown } from '../utils/fees';

function CreateCustomLoan() {
  const navigate = useNavigate();
  const { getMinimumMonthlyPayment, getFinancingTiers, getPaymentDueDayMode, getPaymentDueDayOptions, isDemoMode } = useConfig();
  const demoDisabled = isDemoMode();
  const demoStyle = demoDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {};
  const minimumPayment = getMinimumMonthlyPayment();
  // Two hardcoded 1/15 options used to live in the select below, so an admin
  // creating a loan for a tenant selling the 10th and the 20th could not set
  // either -- and the PATCH route that changes a due day later would have
  // rejected both values this form could produce.
  const dueDayMode = getPaymentDueDayMode();
  const isAnniversaryDueDay = dueDayMode === 'anniversary';
  const offeredDueDays = getPaymentDueDayOptions();
  // In anniversary mode the due day is the day of the month the loan closes, and
  // on this route the closing date IS the first payment date the admin types --
  // the form has no other date on it and created_at is the moment of the save.
  // So the select becomes read-only prose and the server derives from the date.
  const financingTiers = getFinancingTiers();
  const defaultApr = financingTiers.length > 0 ? financingTiers[0].apr : 18;

  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // What escrow a loan on the selected property would be FROZEN with, answered
  // by the server's own helper. Deliberately not derived here: the client never
  // divides a property's annual tax bill by twelve, because origination is the
  // only moment that decides this charge and lib/escrow.js is the only place
  // that computes it. A second implementation in the browser is exactly what
  // migration 051 existed to remove.
  const [escrowPreview, setEscrowPreview] = useState(null);
  const [escrowError, setEscrowError] = useState('');
  
  // Calculate default first payment date (1st or 15th of next month)
  const getDefaultFirstPaymentDate = (dueDay) => {
    const today = new Date();
    let targetDate = new Date(today.getFullYear(), today.getMonth() + 1, parseInt(dueDay));

    // If the target date is less than 14 days away, push to following month
    const daysUntil = Math.ceil((targetDate - today) / (1000 * 60 * 60 * 24));
    if (daysUntil < 14) {
      targetDate = new Date(today.getFullYear(), today.getMonth() + 2, parseInt(dueDay));
    }

    return targetDate.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    userId: '',
    propertyId: '',
    purchasePrice: '',
    downPayment: '0',
    processingFee: '0',
    interestRate: String(defaultApr),
    monthlyPayment: String(minimumPayment),
    paymentDueDay: '1',
    firstPaymentDate: getDefaultFirstPaymentDate('1'),
    paymentMethod: 'cash',
    notes: ''
  });

  // Tenant config arrives asynchronously, so the initial '1' above can be a day
  // this tenant does not offer -- and the create route validates nothing, so the
  // loan would simply be originated on the wrong day. Snap to the first offered
  // day, and move the auto-filled first payment date with it.
  //
  // Not run in anniversary mode: the day comes from the closing date there, so
  // there is no list to snap to.
  useEffect(() => {
    if (isAnniversaryDueDay) return;
    if (!Array.isArray(offeredDueDays) || offeredDueDays.length === 0) return;
    if (offeredDueDays.map(String).includes(formData.paymentDueDay)) return;
    const day = String(offeredDueDays[0]);
    setFormData(prev => ({
      ...prev,
      paymentDueDay: day,
      firstPaymentDate: getDefaultFirstPaymentDate(day)
    }));
    // getDefaultFirstPaymentDate is a stable local helper with no captured state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnniversaryDueDay, offeredDueDays, formData.paymentDueDay]);

  // Real-time loan calculation
  const calculatedLoan = useMemo(() => {
    const purchasePrice = parseFloat(formData.purchasePrice) || 0;
    const downPayment = parseFloat(formData.downPayment) || 0;
    const processingFee = parseFloat(formData.processingFee) || 0;
    const interestRate = parseFloat(formData.interestRate) || 0;
    const monthlyPayment = parseFloat(formData.monthlyPayment) || 0;
    
    // The PROPERTY decides whether the fee belongs in principal. This added it
    // unconditionally while the route ALSO recorded it as a collected
    // processing_fee payment, which is the double charge in miniature.
    const selected = properties.find(p => String(p.id) === String(formData.propertyId));
    const loanAmount = feeBreakdown({
      price: purchasePrice,
      downPayment,
      processingFee,
      feeHandling: selected?.fee_handling
    }).amountFinanced;
    
    if (loanAmount <= 0 || monthlyPayment <= 0) {
      return { loanAmount: 0, calculatedTerm: 0, totalAmount: 0, error: null };
    }
    
    const monthlyRate = interestRate / 100 / 12;
    
    let calculatedTerm;
    let error = null;
    
    if (monthlyRate === 0) {
      // No interest: simple division
      calculatedTerm = Math.ceil(loanAmount / monthlyPayment);
    } else {
      // With interest: amortization formula
      const numerator = 1 - (loanAmount * monthlyRate) / monthlyPayment;
      
      if (numerator <= 0) {
        const minPayment = Math.ceil((loanAmount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -360)));
        error = `Monthly payment too low. Minimum: $${minPayment}`;
        calculatedTerm = 0;
      } else {
        calculatedTerm = Math.ceil(-Math.log(numerator) / Math.log(1 + monthlyRate));
      }
    }
    
    const totalAmount = monthlyPayment * calculatedTerm;
    
    return {
      loanAmount: loanAmount.toFixed(2),
      calculatedTerm,
      totalAmount: totalAmount.toFixed(2),
      error
    };
  }, [formData.purchasePrice, formData.downPayment, formData.processingFee, formData.interestRate, formData.monthlyPayment, formData.propertyId, properties]);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const [customersRes, propertiesRes] = await Promise.all([
        getAllCustomers(),
        getAllProperties()
      ]);
      
      setCustomers(customersRes.data);
      setProperties(propertiesRes.data.filter(p => p.status === 'available'));
    } catch (err) {
      console.error('Load data error:', err);
      alert('Failed to load customers and properties');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePropertyChange = async (propertyId) => {
    const property = properties.find(p => p.id === parseInt(propertyId));
    if (!property) return;

    setFormData({
      ...formData,
      propertyId,
      purchasePrice: property.price
    });

    // Ask the server what this property implies, so the preview quotes the same
    // figure origination will freeze rather than a second opinion about it.
    setEscrowPreview(null);
    setEscrowError('');
    try {
      const { data } = await getPropertyEscrowPreview(propertyId);
      setEscrowPreview(data);
    } catch (err) {
      console.error('Escrow preview error:', err);
      // Never blocks loan creation: this is a preview of a figure the SERVER
      // computes again at insert time from the same source.
      setEscrowError('Could not load this property\'s escrow figures.');
    }
  };

  // The loan this form would create, in the shape the shared helper reads.
  const previewLoan = {
    monthly_payment: formData.monthlyPayment,
    monthly_tax_escrow: escrowPreview?.monthly_tax_escrow ?? 0,
    monthly_hoa_escrow: escrowPreview?.monthly_hoa_escrow ?? 0
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.userId || !formData.propertyId) {
      alert('Please select a customer and property');
      return;
    }
    
    if (calculatedLoan.error) {
      alert(calculatedLoan.error);
      return;
    }
    
    if (window.confirm(`Create custom loan?\n\nLoan Amount: $${calculatedLoan.loanAmount}\nTerm: ${calculatedLoan.calculatedTerm} months\nTotal: $${calculatedLoan.totalAmount}\n\nThe property will be marked as pending.`)) {
      setSubmitting(true);
      try {
        await createCustomLoan({
          ...formData,
          skipPayment: true
        });
        
        alert('Custom loan created successfully!');
        navigate('/admin/loans');
      } catch (err) {
        console.error('Create loan error:', err);
        alert(err.response?.data?.error || 'Failed to create loan');
      } finally {
        setSubmitting(false);
      }
    }
  };
  
  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px' }}>
        <button onClick={() => navigate('/admin/loans')} className="admin-hub-back-btn">
          <span className="admin-hub-back-arrow">←</span>
          Back to Loan Management
        </button>
        <h1 className="admin-page-title">Create Custom Loan</h1>
      </div>
      
      <div style={{ background: 'var(--color-success-bg)', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid var(--color-success-border)' }}>
        <strong>💡 Custom Loans:</strong> Create loans with special terms for loyal customers. 
        Set custom down payments (including $0), interest rates, and monthly payments. 
        No Square payment required.
      </div>
      
      <style>{`
        .loan-creation-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 20px;
        }
        @media (min-width: 1024px) {
          .loan-creation-layout {
            grid-template-columns: 1fr 400px;
          }
        }
      `}</style>
      <div className="loan-creation-layout">
        {/* Left Column: Form */}
        <form onSubmit={handleSubmit} style={{ background: 'var(--color-surface)', padding: '30px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: 'var(--color-heading)', borderBottom: '2px solid var(--color-success-border)', paddingBottom: '10px' }}>Customer & Property</h3>
            
            <div className="form-group">
              <label className="form-label">Customer *</label>
              <select
                value={formData.userId}
                onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                required
                className="form-select"
              >
                <option value="">Select Customer</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.first_name} {customer.last_name} ({customer.email})
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label">Property *</label>
              <select
                value={formData.propertyId}
                onChange={(e) => handlePropertyChange(e.target.value)}
                required
                className="form-select"
              >
                <option value="">Select Property</option>
                {properties.map(property => (
                  <option key={property.id} value={property.id}>
                    {property.title} - ${parseFloat(property.price).toLocaleString()}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: 'var(--color-heading)', borderBottom: '2px solid var(--color-success-border)', paddingBottom: '10px' }}>Loan Terms</h3>
            
            <div className="form-row-2">
              <div className="form-group">
                <label className="form-label">Purchase Price *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.purchasePrice}
                  onChange={(e) => setFormData({ ...formData, purchasePrice: e.target.value })}
                  required
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Down Payment</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.downPayment}
                  onChange={(e) => setFormData({ ...formData, downPayment: e.target.value })}
                  placeholder="0.00"
                  className="form-input"
                />
                <small style={{ color: 'var(--color-text-light)', fontSize: '12px' }}>Set to $0 for no down payment</small>
              </div>
            </div>
            
            <div className="form-row-2">
              <div className="form-group">
                <label className="form-label">Processing Fee</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.processingFee}
                  onChange={(e) => setFormData({ ...formData, processingFee: e.target.value })}
                  placeholder="0.00"
                  className="form-input"
                />
                <small style={{ color: 'var(--color-text-light)', fontSize: '12px' }}>Set to $0 to waive fee</small>
              </div>
              
              <div className="form-group">
                <label className="form-label">Interest Rate (%) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.interestRate}
                  onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })}
                  required
                  className="form-input"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">Monthly Payment *</label>
              <input
                type="number"
                step="0.01"
                value={formData.monthlyPayment}
                onChange={(e) => setFormData({ ...formData, monthlyPayment: e.target.value })}
                required
                className="form-input"
              />
              <small style={{ color: 'var(--color-text-light)', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                <strong>Principal &amp; interest only</strong> &mdash; minimum ${minimumPayment}.
                The term is calculated from this figure. Tax and HOA escrow are collected
                on top of it; the buyer's full monthly payment is in the preview.
              </small>
            </div>
            
            <div className="form-row-2">
              <div className="form-group">
                <label className="form-label">Payment Due Day</label>
                {isAnniversaryDueDay ? (
                  <p className="admin-dueday-prose">
                    Anniversary of closing: the day of the month of the First Payment Date
                    beside this. Set that date and the due day follows it.
                  </p>
                ) : (
                  <select
                    className="admin-dueday-select"
                    value={formData.paymentDueDay}
                    onChange={(e) => setFormData({
                      ...formData,
                      paymentDueDay: e.target.value,
                      firstPaymentDate: getDefaultFirstPaymentDate(e.target.value)
                    })}
                  >
                    {offeredDueDays.map(day => (
                      <option key={day} value={String(day)}>{formatOrdinalDay(day)} of month</option>
                    ))}
                  </select>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">First Payment Date *</label>
                <input
                  type="date"
                  value={formData.firstPaymentDate}
                  onChange={(e) => setFormData({ ...formData, firstPaymentDate: e.target.value })}
                  required
                  className="form-input"
                />
                <small style={{ color: 'var(--color-text-light)', fontSize: '12px' }}>Auto-set based on due day, can override</small>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Payment Method</label>
              <select
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                className="form-select"
              >
                <option value="cash">Cash</option>
                <option value="check">Check</option>
                <option value="venmo">Venmo</option>
                <option value="zelle">Zelle</option>
                <option value="wire_transfer">Wire Transfer</option>
                <option value="money_order">Money Order</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Notes (Internal)</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows="3"
                placeholder="Reason for custom terms, special agreements, etc."
                className="form-textarea"
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '10px', marginTop: '30px', paddingTop: '20px', borderTop: '2px solid var(--color-border)' }}>
            <button type="button" onClick={() => navigate('/admin/loans')} className="btn btn-secondary" style={{ flex: 1 }}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting || calculatedLoan.error || demoDisabled}
              style={{ flex: 1, ...(demoDisabled ? demoStyle : {}) }}
              title={demoDisabled ? 'Disabled in demo mode' : ''}
            >
              {submitting ? 'Creating...' : 'Create Custom Loan'}
            </button>
          </div>
        </form>
        
        {/* Right Column: Live Preview */}
        <div style={{ position: 'sticky', top: '20px', alignSelf: 'start' }}>
          <div style={{ background: 'var(--color-surface)', padding: '25px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', border: '2px solid var(--color-success-border)' }}>
            <h3 style={{ marginBottom: '20px', color: 'var(--color-heading)', fontSize: '20px', textAlign: 'center' }}>📊 Loan Preview</h3>
            
            {calculatedLoan.error ? (
              <div style={{ background: 'var(--color-warning-bg)', border: '1px solid var(--color-warning-border)', borderRadius: '6px', padding: '15px', marginBottom: '15px', color: 'var(--color-warning-text)' }}>
                <strong>⚠️ Error:</strong><br/>
                {calculatedLoan.error}
              </div>
            ) : (
              <>
                <div style={{ background: 'var(--color-success-bg)', padding: '15px', borderRadius: '6px', marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <span style={{ color: 'var(--color-text-light)' }}>Loan Amount:</span>
                    <span style={{ fontWeight: 'bold', fontSize: '18px', color: 'var(--color-heading)' }}>
                      ${parseFloat(calculatedLoan.loanAmount || 0).toLocaleString()}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-light)', marginTop: '5px' }}>
                    (${parseFloat(formData.purchasePrice || 0).toLocaleString()} - ${parseFloat(formData.downPayment || 0).toLocaleString()} + ${parseFloat(formData.processingFee || 0).toLocaleString()})
                  </div>
                </div>
                
                <div style={{ background: 'var(--color-info-bg)', padding: '15px', borderRadius: '6px', marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <span style={{ color: 'var(--color-text-light)' }}>Calculated Term:</span>
                    <span style={{ fontWeight: 'bold', fontSize: '18px', color: 'var(--color-info-text)' }}>
                      {calculatedLoan.calculatedTerm} months
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-light)', marginTop: '5px' }}>
                    Auto-calculated based on loan amount, interest, and monthly payment
                  </div>
                </div>
                
                <div style={{ background: 'var(--color-warning-bg)', padding: '15px', borderRadius: '6px', marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <span style={{ color: 'var(--color-text-light)' }}>Total Amount:</span>
                    <span style={{ fontWeight: 'bold', fontSize: '18px', color: 'var(--color-warning-text)' }}>
                      ${parseFloat(calculatedLoan.totalAmount || 0).toLocaleString()}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-light)', marginTop: '5px' }}>
                    (${parseFloat(formData.monthlyPayment || 0).toFixed(2)} × {calculatedLoan.calculatedTerm} months)
                  </div>
                </div>
                
                <div style={{ background: '#fce4ec', padding: '15px', borderRadius: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <span style={{ color: 'var(--color-text-light)' }}>Total Interest:</span>
                    <span style={{ fontWeight: 'bold', fontSize: '18px', color: '#c2185b' }}>
                      ${(parseFloat(calculatedLoan.totalAmount || 0) - parseFloat(calculatedLoan.loanAmount || 0)).toFixed(2)}
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-light)', marginTop: '5px' }}>
                    Customer will pay this much in interest over {calculatedLoan.calculatedTerm} months
                  </div>
                </div>

                {/* WHAT THE BUYER WILL ACTUALLY OWE EACH MONTH.
                    Everything above this block is about the LOAN -- amount,
                    term, interest. This is about the BILL, and the two are not
                    the same number. The escrow shown here is frozen onto the
                    loan the moment it is created, so this is the last point at
                    which it can be seen before it becomes a contract term. */}
                <div style={{ background: 'var(--color-success-bg)', padding: '15px', borderRadius: '6px', marginTop: '15px', border: '1px solid var(--color-success-border)' }}>
                  <div style={{ fontWeight: 'bold', color: 'var(--color-heading)', marginBottom: '10px' }}>
                    Buyer&rsquo;s Monthly Payment
                  </div>

                  {!formData.propertyId ? (
                    <div style={{ fontSize: '13px', color: 'var(--color-text-light)' }}>
                      Select a property to see its tax and HOA escrow.
                    </div>
                  ) : escrowError ? (
                    <div style={{ fontSize: '13px', color: 'var(--color-warning-text)' }}>{escrowError}</div>
                  ) : escrowPreview && escrowPreview.escrow_enabled === false ? (
                    /* Escrow is switched off for this tenant, so origination will
                       freeze this loan at 0.00 and the buyer will be billed
                       principal and interest only. Saying so is the point: the
                       preview must state what will actually be frozen, and an
                       escrow line quoting a charge that never arrives would be
                       the same defect as a charge arriving with no preview. */
                    <div style={{ fontSize: '13px', color: 'var(--color-text-light)' }}>
                      Escrow collection is switched off for this account, so this loan
                      will be created with no tax or HOA escrow. The buyer pays
                      principal and interest only.
                      <div style={{ marginTop: '8px', fontWeight: 'bold', color: 'var(--color-heading)' }}>
                        Monthly Payment: ${parseFloat(formData.monthlyPayment || 0).toFixed(2)}
                      </div>
                    </div>
                  ) : !escrowPreview ? (
                    <div style={{ fontSize: '13px', color: 'var(--color-text-light)' }}>Loading escrow&hellip;</div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '14px' }}>
                        <span style={{ color: 'var(--color-text-light)' }}>Principal &amp; Interest:</span>
                        <span>${parseFloat(formData.monthlyPayment || 0).toFixed(2)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '14px' }}>
                        <span style={{ color: 'var(--color-text-light)' }}>Tax Escrow:</span>
                        <span>${parseFloat(escrowPreview.monthly_tax_escrow || 0).toFixed(2)}</span>
                      </div>
                      {parseFloat(escrowPreview.monthly_hoa_escrow) > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '14px' }}>
                          <span style={{ color: 'var(--color-text-light)' }}>HOA Escrow:</span>
                          <span>${parseFloat(escrowPreview.monthly_hoa_escrow).toFixed(2)}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--color-success-border)', paddingTop: '8px', marginTop: '8px' }}>
                        <span style={{ fontWeight: 'bold', color: 'var(--color-heading)' }}>Monthly Payment:</span>
                        <span style={{ fontWeight: 'bold', fontSize: '18px', color: 'var(--color-heading)' }}>
                          ${totalMonthlyPayment(previewLoan).toFixed(2)}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--color-text-light)', marginTop: '8px' }}>
                        Frozen onto the loan when it is created. Editing this property&rsquo;s
                        tax or HOA figures afterwards will not change what this buyer pays.
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
            
            <div style={{ marginTop: '20px', padding: '15px', background: 'var(--color-surface-sunken)', borderRadius: '6px', fontSize: '13px', color: 'var(--color-text-light)' }}>
              <strong style={{ color: 'var(--color-text)' }}>ℹ️ How it works:</strong><br/>
              The term is automatically calculated using the amortization formula based on your monthly payment amount and interest rate.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateCustomLoan;